import { Flex, Icon, Text } from '@homebotapp/hb-react-component-catalog'
import { formatAbbrCurrency, formatCurrency } from '../../../helpers/formatters'
import { SavedSearchArea } from '../../../types/savedSearches'
import { ListingCompact, ListingsFilters } from '../../../api/gqlaxy/gql/generated/graphql'
import { defineMessages, useIntl } from 'react-intl'
import compact from 'lodash/compact'
import isNumber from 'lodash/isNumber'

export const CriteriaText = ({ children }: { children: React.ReactNode }) => (
  <Text size='sm' mb={0}>
    {children}
  </Text>
)

export const getBathsDisplay = (minBedroomsCount: number | null | undefined) => {
  if (minBedroomsCount !== null && minBedroomsCount !== undefined) {
    // savedSearchTodo - translate this using useIntl
    return <CriteriaText>{`${minBedroomsCount}+ baths`}</CriteriaText>
  } else {
    return null
  }
}

export const getPriceRangeDisplay = (minPrice: number | null | undefined, maxPrice: number | null | undefined) => {
  // if user set both min and max price, show range
  const hasMinPrice = minPrice !== null && minPrice !== undefined
  const hasMaxPrice = maxPrice !== null && maxPrice !== undefined
  if (hasMinPrice && hasMaxPrice) {
    return (
      <CriteriaText>
        {formatAbbrCurrency(minPrice)} - {formatAbbrCurrency(maxPrice)}
      </CriteriaText>
    )
    // if user only set a min price
  } else if (hasMinPrice && !hasMaxPrice) {
    return <CriteriaText>{`${formatAbbrCurrency(minPrice)}+`}</CriteriaText>
    // if user only set a max price
    // savedSearchTodo - translate this
  } else if (hasMaxPrice && !hasMinPrice) {
    return <CriteriaText>{`Up to ${formatAbbrCurrency(maxPrice)}`}</CriteriaText>
    // user did not set a min or max price
  } else {
    return null
  }
}

export const getBedsDisplay = (minBedroomsCount: number | null | undefined) => {
  if (minBedroomsCount !== null && minBedroomsCount !== undefined) {
    // savedSearchTodo - translate this
    return <CriteriaText>{`${minBedroomsCount}+ beds`}</CriteriaText>
  } else {
    return null
  }
}

const MSG = defineMessages({
  customAreaNear: {
    id: 'HomeSearch.savedSearches.customAreaNear',
    defaultMessage: 'Custom area near {location}'
  }
})
export const SearchArea = ({
  searchArea,
  firstListing
}: {
  searchArea: SavedSearchArea | undefined
  firstListing: ListingCompact
}) => {
  const intl = useIntl()
  if (!searchArea) return null
  {
    // the name of the search area is not stored in the saved search (we either store a bounding box or a placeId)
    // if we store a PLACE_ID, the user was searching in a city or zip code. If we don't have a placeId, we have a custom search area aka bounding box
  }
  const getSearchAreaDisplayText = () => {
    if (!firstListing) return null

    if (searchArea.placeIdInput?.placeId) {
      return `${firstListing?.address?.city}, ${firstListing?.address?.stateOrProvince}`
    } else {
      return intl.formatMessage(MSG.customAreaNear, {
        location: `${firstListing?.address?.city}, ${firstListing?.address?.stateOrProvince}`
      })
    }
  }

  return (
    <Flex alignItems='center' mb={1}>
      <Icon width='12px' height='12px' name='map-marker-mdc' />
      <Text fontWeight='semibold' size='sm' mb={0} ml={2}>
        {getSearchAreaDisplayText()}
      </Text>
    </Flex>
  )
}

// takes inspiration from selectFiltersCount in selectors/filters.ts
export const getChangedCriteriaCount = (filterOrSavedSearch: ListingsFilters | undefined) => {
  if (!filterOrSavedSearch) return undefined

  const priceFilters = compact([filterOrSavedSearch.minPrice, filterOrSavedSearch.maxPrice])
  const sqftFilters = compact([filterOrSavedSearch.minSqft, filterOrSavedSearch.maxSqft])
  const acreFilters = compact([filterOrSavedSearch.minAcres, filterOrSavedSearch.maxAcres])
  const yearBuiltFilters = compact([filterOrSavedSearch.minYearBuilt, filterOrSavedSearch.maxYearBuilt])
  const monthlyExpenseFilters = compact([
    filterOrSavedSearch.monthlyExpensesInput?.minMonthlyExpenses,
    filterOrSavedSearch.monthlyExpensesInput?.maxMonthlyExpenses,
    filterOrSavedSearch.monthlyExpensesInput?.downpaymentAmountDollars
  ])

  const filterTypes = {
    priceFilters: priceFilters,
    sqftFilters: sqftFilters,
    acreFilters: acreFilters,
    yearBuiltFilters: yearBuiltFilters,
    monthlyExpenseFilters: monthlyExpenseFilters,
    minBathroomsCount: filterOrSavedSearch.minBathroomsCount,
    minBedroomsCount: filterOrSavedSearch.minBedroomsCount,
    propertyTypes: filterOrSavedSearch.propertyTypes,
    statuses: filterOrSavedSearch.statuses,
    hasSellerConcession: filterOrSavedSearch.hasSellerConcession,
    isAssumable: filterOrSavedSearch.isAssumable,
    hasOpenHouses: filterOrSavedSearch.hasOpenHouses
  }

  let totalChangedCriteriaCount = 0
  let changedCriteriaKeys: string[] = []
  let additionalChangedCriteriaCountExcludingPriceBedsAndBaths = 0
  let priceBedsOrBathsWasModified = false

  Object.entries(filterTypes).forEach(([key, value]) => {
    if (
      isNumber(value) ||
      (Array.isArray(value) && value.length > 0) ||
      (typeof value === 'boolean' && value === true) // for hasSellerConcession and isAssumable
    ) {
      totalChangedCriteriaCount++
      changedCriteriaKeys.push(key)
      if (key !== 'priceFilters' && key !== 'minBedroomsCount' && key !== 'minBathroomsCount') {
        additionalChangedCriteriaCountExcludingPriceBedsAndBaths++
      } else {
        priceBedsOrBathsWasModified = true
      }
    }
  })

  const displayObject = {
    totalChangedCriteriaCount,
    changedCriteriaKeys,
    additionalChangedCriteriaCountExcludingPriceBedsAndBaths,
    priceBedsOrBathsWasModified
  }

  return displayObject
}
